import { api, deviceRegistrationFor, type Session } from '@/shared/api';
import { unwrap } from '@/shared/api/envelope';

import {
    authenticatedSessionSchema,
    invitationPreviewSchema,
    meSchema,
    sessionFromAuthentication,
    toInvitationPreview,
    tourSchema,
    type InvitationPreview,
    type Me,
} from './model';

export interface LoginInput {
    email: string;
    password: string;
    /** Uuid of slug; kiest die omgeving meteen. Zonder actief lidmaatschap: 403. */
    tenant?: string;
}

export interface ResetPasswordInput {
    token: string;
    email: string;
    password: string;
    passwordConfirmation: string;
}

export interface AcceptInvitationInput {
    /** Het adres uit de uitnodiging; bepaalt de apparaat-identiteit. */
    email: string;
    name?: string;
    password?: string;
    passwordConfirmation?: string;
}

/** Verzoeken zonder sessie: geen token, geen omgeving, niets in de offline cache. */
const ANONYMOUS = { skipAuth: true, cacheKey: false } as const;

/**
 * Verzoeken namens één bepaalde sessie. Het token gaat expliciet mee, zodat een
 * lopende impersonatie er niet tussen komt, en de omgeving volgt uit het token.
 */
function as(session: Pick<Session, 'token'>) {
    return { token: session.token, cacheKey: false } as const;
}

/**
 * Inloggen, sessie, omgeving, wachtwoord en uitnodigingen
 * (`smartkeur-api/docs/02-api-contract.md` §Auth & identiteit).
 *
 * Alles wat een sessie oplevert, levert een `Session` op - nooit de ruwe vorm.
 */
export const authApi = {
    async login(input: LoginInput): Promise<Session> {
        const email = input.email.trim();
        const body = {
            email,
            password: input.password,
            device: await deviceRegistrationFor(email),
            ...(input.tenant ? { tenant: input.tenant } : {}),
        };

        return sessionFromAuthentication(
            unwrap(authenticatedSessionSchema, await api.post('/auth/login', body, ANONYMOUS)),
        );
    },

    /** Wie ben je nu, waar werk je en wat mag je - voor het herstellen van een sessie. */
    async me(session: Pick<Session, 'token'>): Promise<Me> {
        return unwrap(meSchema, await api.get('/auth/me', as(session)));
    },

    /**
     * De rondleiding is afgemaakt of overgeslagen: voor deze persoon op elk apparaat.
     * Geeft het moment dat de API heeft vastgelegd.
     */
    async completeTour(session: Pick<Session, 'token'>): Promise<string | null> {
        return unwrap(tourSchema, await api.post('/auth/me/tour', undefined, as(session)))
            .tour_completed_at;
    },

    /**
     * Naar een andere omgeving. Levert een nieuw token; het vorige token van dit
     * apparaat vervalt meteen. `remember` maakt dit de omgeving die na de
     * volgende keer inloggen opent.
     */
    async switchTenant(
        session: Pick<Session, 'token'>,
        tenant: string,
        options: { remember?: boolean } = {},
    ): Promise<Session> {
        return sessionFromAuthentication(
            unwrap(
                authenticatedSessionSchema,
                await api.post(
                    '/auth/switch-tenant',
                    { tenant, ...(options.remember ? { remember: true } : {}) },
                    as(session),
                ),
            ),
        );
    },

    /** Verlengt het token zolang het apparaat vertrouwd is. */
    async refresh(session: Pick<Session, 'token'>): Promise<Session> {
        return sessionFromAuthentication(
            unwrap(
                authenticatedSessionSchema,
                await api.post('/auth/refresh', undefined, as(session)),
            ),
        );
    },

    /** Trekt het token in. */
    async logout(session: Pick<Session, 'token'>): Promise<void> {
        await api.post('/auth/logout', undefined, { ...as(session), expect: 'none' });
    },

    /** Antwoordt altijd hetzelfde, of het adres nu bestaat of niet. */
    async forgotPassword(email: string): Promise<void> {
        await api.post(
            '/auth/forgot-password',
            { email: email.trim() },
            { ...ANONYMOUS, expect: 'none' },
        );
    },

    async resetPassword(input: ResetPasswordInput): Promise<void> {
        await api.post(
            '/auth/reset-password',
            {
                token: input.token,
                email: input.email.trim(),
                password: input.password,
                password_confirmation: input.passwordConfirmation,
            },
            { ...ANONYMOUS, expect: 'none' },
        );
    },

    /** Publiek: welk bedrijf, welke rol, en is er een wachtwoord nodig. */
    async invitation(token: string): Promise<InvitationPreview> {
        return toInvitationPreview(
            unwrap(
                invitationPreviewSchema,
                await api.get(`/auth/invitations/${encodeURIComponent(token)}`, ANONYMOUS),
            ),
        );
    },

    /**
     * Publiek: accepteren is meteen inloggen. Bewust zonder token - wie hier al
     * ingelogd is als iemand anders, mag de uitnodiging niet naar zich toe
     * trekken; de API koppelt hem dan aan het adres uit de uitnodiging.
     */
    async acceptInvitation(token: string, input: AcceptInvitationInput): Promise<Session> {
        const body = {
            ...(input.name?.trim() ? { name: input.name.trim() } : {}),
            ...(input.password
                ? {
                      password: input.password,
                      password_confirmation: input.passwordConfirmation ?? '',
                  }
                : {}),
            device: await deviceRegistrationFor(input.email),
        };

        return sessionFromAuthentication(
            unwrap(
                authenticatedSessionSchema,
                await api.post(
                    `/auth/invitations/${encodeURIComponent(token)}/accept`,
                    body,
                    ANONYMOUS,
                ),
            ),
        );
    },
};
